import { useSelector } from 'react-redux';
import { useEffect, useRef, useState } from 'react';
import { errorMessages } from '~/constants/errorMessages.ts';
import { useRetryAlert } from '../hooks/useRetryAlert.ts';
import { useAppDispatch } from '~/store/appDispatch.ts';
import { DiiaVerification } from '~/components/SignatureVerification/DiiaVerification/DiiaVerification.tsx';
import type { TimerRefHandle } from '~/components/SignatureVerification/DiiaQRCode.tsx';
import ModalAlert from '~/components/modals/ModalAlert/ModalAlert';
import { getVerificationStatusRevoke } from '~/store/otp-verification/thunks/personalDataVerificationThunks.ts';
import { RootState } from '~/store/store.ts';
import { revokeGetApplicationForm } from '~/store/processing-personal/thunks/getApplicationFormThunks.ts';
import { DiiaStatuses, TDiiaInitializeResponse } from '~/store/diia/types.ts';
import { SerializedError } from '@reduxjs/toolkit';
import { setDiiaSignError, setDiiaSignStatus } from '~/store/diia/slices.ts';
import {
  revokeGetFinalStatusDiiaVerificationProcessingPersonal,
  revokeGetStatusDiiaVerificationProcessingPersonal,
  revokeInitializeDiiaVerificationProcessingPersonal,
} from '~/store/diia/thunks/verificationProcessingPersonalThunks.ts';
import FullPageLoader from '~/components/FullPageLoader/FullPageLoader';
import { setPersonalDataApplicationsError } from '~/store/processing-personal/slices.ts';
import { setOtpError } from '~/store/otp-verification/slices.ts';
import { SignatureError } from '~/modules/processing-personal/PersonalDataApplicationForm/SignatureError.ts';
import OTPCodeModal from '~/components/OTPCodeModal/OTPCodeModal.tsx';
import { TErrorResponse } from '~/store/types.ts';
import { QR_CODE_TIME } from '~/constants/common.ts';
import { setUserByUrlToken } from '~/store/users/slices.ts';

interface ProcessClientVerificationRevokeProps {
  onSuccess?: () => void;
  isLoading: (loading: boolean) => void;
}

export const ProcessClientVerificationRevoke = ({
  onSuccess,
  isLoading,
}: ProcessClientVerificationRevokeProps) => {
  const dispatch = useAppDispatch();

  const token = useSelector((state: RootState) => state.users.token);
  const { applicationId } = useSelector((state: RootState) => state.users.user.tokenData) || {};

  const {
    isVerified,
    error: otpError,
    phone,
  } = useSelector((state: RootState) => state.otpVerification) || {};
  const {
    deepLink,
    loading: isDiiaLoading,
    error: diiaError,
    status,
  } = useSelector((state: RootState) => state.diia) || {};
  const { loading: isProcessingPersonalLoading, error: processingPersonalError } = useSelector(
    (state: RootState) => state.processingPersonal
  );

  const isLoadingStore = isDiiaLoading || isProcessingPersonalLoading;
  const error = diiaError || processingPersonalError;

  const alert = useRetryAlert();
  const isTimeoutRef = useRef<boolean>(false);
  const timerRef = useRef<TimerRefHandle>(null);

  const [modalOTPVerification, setModalOTPVerification] = useState<boolean>(false);
  const [modalDiiaVerification, setModalDiiaVerification] = useState<boolean>(false);
  const [otpErrorLocal, setOtpErrorLocal] = useState<string>('');
  const [requestId, setRequestId] = useState<string>('');
  const [getStatusPromise, setGetStatusPromise] = useState<{ abort: () => void } | null>(null);

  useEffect(() => {
    if (modalDiiaVerification && timerRef.current) {
      timerRef.current.startTimer();
    }
  }, [modalDiiaVerification]);

  useEffect(() => {
    dispatch(setUserByUrlToken());
  }, [dispatch]);

  useEffect(() => {
    if (applicationId) {
      setModalOTPVerification(true);
      isLoading(false);
      dispatch(getVerificationStatusRevoke({ appId: applicationId, token: token }))
        .unwrap()
        .catch(() => {
          dispatch(setOtpError(errorMessages.SERVICE_UNAVAILABLE));
        });
    }
  }, [applicationId]);

  useEffect(() => {
    if (status === DiiaStatuses.rejected && requestId) {
      dispatch(
        revokeGetFinalStatusDiiaVerificationProcessingPersonal({
          token,
          requestId,
          applicationId,
        })
      )
        .then((data) => {
          if (!(data.payload as TErrorResponse).errorCode) {
            setModalDiiaVerification(false);
            getFormApplication();
          }
        })
        .catch(() => {
          dispatch(setDiiaSignStatus({ status: DiiaStatuses.canceled }));
        });
    }
  }, [status, requestId]);

  const closeAlertModal = () => {
    diiaError && dispatch(setDiiaSignError(''));
    getStatusPromise?.abort();
    processingPersonalError && dispatch(setPersonalDataApplicationsError(''));
    otpError && dispatch(setOtpError(''));
  };

  const switchToOTP = (): void => {
    setModalDiiaVerification(false);
    setModalOTPVerification(true);
  };

  const getFormApplication = (): void => {
    try {
      if (isLoadingStore) return;
      dispatch(revokeGetApplicationForm({ applicationId }))
        .unwrap()
        .then(() => {
          if (onSuccess) onSuccess();
        })
        .catch(() => {
          dispatch(setPersonalDataApplicationsError(errorMessages.SERVICE_UNAVAILABLE));
        });
    } catch (err) {
      dispatch(setPersonalDataApplicationsError(errorMessages.SERVICE_UNAVAILABLE));
    }
  };

  const startDiiaVerification = async () => {
    if (isLoadingStore) return;
    setModalDiiaVerification(false);
    try {
      const { payload } = await dispatch(
        revokeInitializeDiiaVerificationProcessingPersonal({
          applicationId,
          token,
        })
      );
      const requestId = (payload as TDiiaInitializeResponse)?.requestId;
      setRequestId(requestId);
      timerRef.current?.startTimer();
      if (isLoadingStore) return;
      setModalDiiaVerification(true);

      const promise = dispatch(
        revokeGetStatusDiiaVerificationProcessingPersonal({
          token,
          requestId,
          applicationId,
        })
      );
      setGetStatusPromise(promise);
      const response = await promise;
      if ((response as { error: SerializedError }).error) return;
      setGetStatusPromise(null);

      if ((response.payload as { status: string })?.status === DiiaStatuses.notUsed) {
        dispatch(setDiiaSignError(errorMessages.DOCUMENT_SIGNING_FAILED));
      }
      if (isLoadingStore) return;
      if ((response.payload as { status: string })?.status === DiiaStatuses.processing) {
        if (isLoadingStore) return;
        const { payload: finalStatus } = await dispatch(
          revokeGetFinalStatusDiiaVerificationProcessingPersonal({
            token,
            requestId,
            applicationId,
          })
        );

        if ((finalStatus as { status: string }).status === DiiaStatuses.done) {
          getFormApplication();
        }
      } else {
        setModalDiiaVerification(false);
        getFormApplication();
      }
    } catch (error) {
      setModalDiiaVerification(false);
      SignatureError(error);
    }
  };

  const handleQrTimeout = (): void => {
    isTimeoutRef.current = true;
    setModalDiiaVerification(false);
    alert.handleTimeout(undefined, switchToOTP);
  };

  const handleValidationSuccess = () => {
    setModalOTPVerification(false);
    if (!isVerified) {
      startDiiaVerification();
    } else {
      getFormApplication();
    }
  };

  return (
    <>
      <OTPCodeModal
        modalOTPVerification={modalOTPVerification}
        phone={phone}
        error={otpErrorLocal}
        setError={setOtpErrorLocal}
        onValidationSuccess={handleValidationSuccess}
        token={token}
      />

      <DiiaVerification
        modalDiiaVerification={modalDiiaVerification}
        deepLink={deepLink}
        timerRef={timerRef}
        qrCodeTimer={QR_CODE_TIME}
        handleCloseModal={handleQrTimeout}
      />

      {isLoadingStore && <FullPageLoader />}

      {!!error && <ModalAlert isOpen={!!error} text={error} onClose={closeAlertModal} />}
    </>
  );
};
